'use client';

import { cn } from '@/lib/utils';
import { Download, Loader2 } from 'lucide-react';
import { formatTime } from '../utils/date-utils';
import { AttachmentDropdown } from './AttachmentDropdown';
import { useCorrespondenceExport } from '@/lib/services/correspondence-exports';
import { parseHtml } from '@/lib/utils/sanitize-html';
import type { ChatMessage as ChatMessageType } from '../types';

/**
 * Escape HTML special characters then convert plain-text URLs into anchor tags.
 * The result is safe to pass to parseHtml() which will sanitize and render.
 */
function linkifyContent(text: string) {
  // Escape HTML entities so raw content is safe
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  // Replace URLs with anchor tags
  const linked = escaped.replace(
    /https?:\/\/[^\s<>"'&]+(?:&amp;[^\s<>"'&]+)*/g,
    (url) => {
      // Restore &amp; back to & for the href
      const href = url.replace(/&amp;/g, '&');
      return `<a href="${href}">${url}</a>`;
    }
  );

  return parseHtml(linked);
}

interface ChatMessageProps {
  message: ChatMessageType;
  matterId?: number;
  showTimestamp?: boolean;
  isGrouped?: boolean;
  onSendDraft?: (messageId: string) => void;
  onDeleteDraft?: (messageId: string) => void;
  onEditDraft?: (messageId: string) => void;
  isBeingEdited?: boolean;
  threadColor?: string;
  threadName?: string;
  showThreadIndicator?: boolean;
  institutionFaviconUrl?: string;
}

export function ChatMessage({
  message,
  matterId,
  showTimestamp = true,
  isGrouped = false,
  onSendDraft: _onSendDraft,
  onDeleteDraft: _onDeleteDraft,
  onEditDraft: _onEditDraft,
  isBeingEdited: _isBeingEdited = false,
  threadColor,
  threadName,
  showThreadIndicator = false,
  institutionFaviconUrl: _institutionFaviconUrl
}: ChatMessageProps) {
  // Visual distinction based on direction:
  // - 'outbound' = Messages FROM law firm TO institution (shown on RIGHT, primary color)
  // - 'inbound' = Messages FROM institution TO law firm (shown on LEFT, gray color)
  const isUserMessage = message.direction === 'outbound';

  // Correspondence export hook - only enabled when matterId is provided
  const { createExport, isCreating, progress } = useCorrespondenceExport(
    matterId || 0,
    message.id
  );

  // Get initials for avatar
  const _getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div
      className={cn(
        'relative flex',
        isUserMessage ? 'justify-end' : 'justify-start',
        isGrouped ? 'mt-1' : 'mt-5' // Good spacing between messages
      )}
    >
      {/* Thread indicator bar - now on right for user messages */}
      {isUserMessage && threadColor && (
        <div className="absolute top-0 right-0 bottom-0 mt-2 flex items-start">
          <div
            className={cn(
              'w-1 rounded-full transition-all duration-200',
              showThreadIndicator ? 'opacity-100' : 'opacity-30'
            )}
            style={{
              backgroundColor: threadColor,
              height: isGrouped ? 'calc(100% - 0.5rem)' : '100%'
            }}
          />
        </div>
      )}

      <div
        className={cn(
          'group relative max-w-[70%]',
          message.isSending && 'opacity-60'
        )}
      >
        <div
          className={cn(
            'relative',
            isUserMessage
              ? 'bg-primary text-primary-foreground rounded-2xl rounded-br-md'
              : 'bg-muted text-foreground rounded-2xl rounded-bl-md',
            'transition-all duration-200'
          )}
        >
          {/* Message content container */}
          <div className="px-4 py-2.5">
            {/* Subject line at top of message bubble - use message's own subject if available */}
            {(message.subject || threadName) && !isGrouped && (
              <div
                className={cn(
                  'mb-2 border-b pb-2 text-xs font-medium',
                  isUserMessage
                    ? 'text-primary-foreground/80 border-primary-foreground/20'
                    : 'border-border/50 text-foreground-secondary'
                )}
              >
                Subject: {message.subject || threadName}
              </div>
            )}
            <p className="text-sm break-words whitespace-pre-wrap">
              {linkifyContent(message.content)}
            </p>

            {/* Attachments */}
            {message.attachments && message.attachments.length > 0 && (
              <div className="mt-2 space-y-1">
                {message.attachments.map((attachment) => (
                  <AttachmentDropdown
                    key={attachment.id}
                    attachment={attachment}
                    isUserMessage={isUserMessage}
                    className="rounded"
                  />
                ))}
              </div>
            )}

            {/* Footer with timestamp, download, and status */}
            {showTimestamp && (
              <div
                className={cn(
                  'mt-1 flex items-center gap-1.5',
                  isUserMessage ? 'justify-start' : 'justify-end'
                )}
              >
                {message.isSending ? (
                  <span className="text-primary-foreground/70 flex items-center gap-1.5">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span className="text-12px">Sending...</span>
                  </span>
                ) : (
                  <>
                    <span
                      className={cn(
                        'text-12px',
                        isUserMessage
                          ? 'text-primary-foreground/60'
                          : 'text-muted-foreground'
                      )}
                    >
                      {formatTime(message.timestamp)}
                    </span>

                    {/* Download correspondence button - only show for sent messages and when matterId is available */}
                    {matterId && !message.isDraft && (
                      <button
                        onClick={() => createExport()}
                        disabled={
                          isCreating || progress.status === 'processing'
                        }
                        className={cn(
                          'text-12px ms-auto cursor-pointer transition-opacity hover:underline disabled:cursor-not-allowed disabled:opacity-50',
                          isUserMessage
                            ? 'text-primary-foreground'
                            : 'text-foreground'
                        )}
                        title="Download correspondence as ZIP with email subject, body, and attachments"
                      >
                        {isCreating || progress.status === 'processing' ? (
                          <>
                            <Download className="mr-1.5 inline h-3 w-3 animate-pulse" />
                            Preparing...
                          </>
                        ) : (
                          <>
                            <Download className="mr-1.5 inline h-3 w-3" />
                            Download Correspondence
                          </>
                        )}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
